import generateColumnDefByField from '@/common-utils/functions/generateColumnDefByField';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import RenderBankCode from '@/app/modules/contracts/components/bankCode/RenderBankCode';
import RenderApplication from '@/app/modules/contracts/components/application/RenderApplication';
import Entity from '@/common-utils/components/renders/entity/Entity';
import RenderPosTerminalConnectionType from '../../renders/connectionType/RenderPosTerminalConnectionType';
import OPERATION_TYPE from '../../../enums/OperationType';
import { Badge } from '@/common-utils/components/ui/badge';
import { nsTerminal } from '@/app/i18n/namespaces';
import RenderEntryPort from '../../renders/entryPort/RenderEntryPort';
import PosTerminalLocation from '../../location/PosTerminalLocation';
import eventLogsFields from '../../../fields/eventLogFields';
import RenderDateWithFromTime from '@/common-utils/components/renders/date/RenderDateWithFromTime';
import LegalInfosCell from '../../modals/legalInfos/LegalInfosCell';
import genUID from '@/common-utils/functions/generateUniqueId';

export const columnsTerminalEventLogsId = 'terminalEventLogs';

export const columnsTerminalEventLogs = (headerPackage) => {

    return ([
        generateColumnDefByField({
            field: eventLogsFields.NETWORK_INFORMATION,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const connectionType = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.DEVICE_INFORMATION.NETWORK_INTERFACE_TYPE));
                const sourceIp = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.NETWORK_INFORMATION.SOURCE_ADDRESS));
                const posTerminalUuid = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.DEVICE_INFORMATION.POS_TERMINAL_UUID));

                return (
                    cell &&
                    <PosTerminalLocation
                        connectionType={connectionType}
                        sourceAddressIp={sourceIp}
                        posTerminalUuid={posTerminalUuid}
                    />
                );
            },
            show: !headerPackage.selectionMode
        }),
        generateColumnDefByField({
            field: eventLogsFields.METRICS_INFORMATION.STARTED_AT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const startedAt = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.METRICS_INFORMATION.STARTED_AT));
                return (
                    cell &&
                    <RenderDateWithFromTime value={startedAt} />
                );
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.OPERATIONS,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const callDuration = (getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.METRICS_INFORMATION.DURATION)) / 1000).toFixed(0);
                const operations = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.FINANCIAL_INFORMATION.OPERATIONS))?.map((item) => ({
                        name: item,
                        key: genUID()
                    }));

                return (
                    cell && cell.getValue(eventLogsFields.METRICS_INFORMATION.STARTED_AT.key) ?
                        <div>
                            <div>
                                {
                                    operations.map((operation) =>
                                        operation.name === OPERATION_TYPE.BLACKLISTED_POS_TERMINAL.name ?
                                            <Badge className='badge-danger' key={operation.key}>
                                                <Entity className='block' entity={OPERATION_TYPE[operation.name].entity}
                                                    namespace={OPERATION_TYPE[operation.name].namespace} />
                                            </Badge>
                                            :
                                            <Entity className='block' key={operation.key}
                                                entity={OPERATION_TYPE[operation.name].entity}
                                                namespace={OPERATION_TYPE[operation.name].namespace} />
                                    )
                                }
                            </div>
                            <small>
                                {callDuration < 0.5 ? '1 sec.' : `${callDuration} sec.`}
                            </small>
                        </div>
                        : false
                );
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.APPLICATION,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderApplication applicationCode={cell.getValue(eventLogsFields.APPLICATION.key)} />
            )
        }),
        generateColumnDefByField({
            field: eventLogsFields.VERSION_CB,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell && cell.getValue(eventLogsFields.VERSION_CB.key) &&
                <span>{cell.getValue(eventLogsFields.VERSION_CB.key).replace(/^CB/, '')}</span>
            )
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.ACCEPTANCE_AGREEMENT_NUMBER,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const acceptanceAgreementNumber = cell.getValue(eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.ACCEPTANCE_AGREEMENT_NUMBER.key);
                const isTestMerchantContract = acceptanceAgreementNumber && acceptanceAgreementNumber.startsWith('1999');
                return (
                    cell &&
                        isTestMerchantContract ?
                        <div>
                            <div>{acceptanceAgreementNumber}</div>
                            <small>
                                <Entity entity={'event_logs.financial_information.acceptance_system_information.test_contract'} namespace={nsTerminal} />
                            </small>
                        </div>
                        : acceptanceAgreementNumber ?
                            <div>{acceptanceAgreementNumber}</div>
                            :
                            null
                );
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.ACCEPTANCE_SYSTEM_NUMBER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.BANK_CODE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const acceptanceSystemInformation = cell.row.original.financialInformation && cell.row.original.financialInformation.acceptanceSystemInformation;
                const bankCode = cell && cell.getValue(eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.BANK_CODE.key) ?
                    cell.getValue(eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.BANK_CODE.key)
                    : cell && acceptanceSystemInformation && acceptanceSystemInformation.acquirerIdentifier ?
                        acceptanceSystemInformation.acquirerIdentifier.substr(-5)
                        : '';
                return (
                    cell &&
                    <RenderBankCode bankCode={bankCode} />
                );
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.DESTINATION_INFORMATION.ADDRESS.X121_ADDRESS,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.COMPANY_REGISTRATION_NUMBER,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const registrationNumber = cell.getValue(eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.COMPANY_REGISTRATION_NUMBER);
                return registrationNumber && <LegalInfosCell registrationNumber={registrationNumber} />;
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.APPLICATION_ID,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: eventLogsFields.DEVICE_INFORMATION.NETWORK_INTERFACE_TYPE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const simCardNumber = (getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.DEVICE_INFORMATION.ICCID)));
                const lastIccidOperator = (getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(eventLogsFields.DEVICE_INFORMATION.ICCID_OPERATOR)));
                return (
                    <RenderPosTerminalConnectionType
                        connectionType={cell.getValue(eventLogsFields.DEVICE_INFORMATION.NETWORK_INTERFACE_TYPE.key)}
                        simNumber={simCardNumber}
                        iccidOperators={lastIccidOperator}
                        showDefaultOperatorMessage={false}
                    />
                );
            }
        }),
        generateColumnDefByField({
            field: eventLogsFields.NETWORK_INFORMATION.ENTRY_PORT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderEntryPort cell={cell} />
            )
        })
    ]);
};